import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type CSSProperties,
} from "react";
import Actions from "./Actions";
import Paragraph from "./Paragraph";
import PerfReadout from "./PerfReadout";
import WordEditor from "./WordEditor";
import { useGesture } from "../hooks/useGesture";
import { useWordCursor } from "../hooks/useWordCursor";
import { CONTENT_ELEMENT_ID } from "../lib/export";
import { isPerfEnabled } from "../lib/perf";
import { markDelay, settleDuration } from "../lib/motion";
import { endHaptics, tickHaptic } from "../lib/platform";
import {
  WORD_ID_ATTRIBUTE,
  isElementInViewport,
  randomSample,
  resolveElementId,
} from "../lib/viewport";
import { memoizeLayout } from "../state/layout";
import type { Action, ErasureDocument } from "../state/reducer";
import { isWord, type Character, type Word as WordType } from "../state/types";

/** Fraction of the visible unmarked words the dice button erases. */
export const RANDOM_ERASE_FRACTION = 0.2;

interface EditorProps {
  document: ErasureDocument;
  dispatch: React.Dispatch<Action>;
  canUndo: boolean;
  canRedo: boolean;
  onClose: () => void;
}

const Editor = ({
  document: erasure,
  dispatch,
  canUndo,
  canRedo,
  onClose,
}: EditorProps) => {
  const { page, style, meta } = erasure;
  const [editedWord, setEditedWord] = useState<WordType | null>(null);

  /**
   * Whether the page is still in its opening beat.
   *
   * On the way in, a saved erasure opens as the page it was made from and its
   * marks are then settled onto it in front of the reader. Only on the way in:
   * after that a mark is drawn the instant the finger reaches the word. Every
   * mark used to play the settle, and a word that takes a quarter of a second
   * to finish erasing reads as the stroke trailing the finger — it was also a
   * main-thread animation of colour, restyling and repainting every word still
   * settling on every frame of a stroke. This is presentation only; the marks
   * themselves are the reducer's.
   */
  const [settling, setSettling] = useState(() => settleDuration() > 0);

  useEffect(() => {
    const duration = settleDuration();
    if (duration === 0) return;
    // Fires after the entrance is over, so no animation is cut short by it.
    const timer = window.setTimeout(() => setSettling(false), duration);
    return () => window.clearTimeout(timer);
  }, []);

  // Rebuilt only when the page's structure changes, which marking never does:
  // the gesture layer's lookups stay O(1) on pages with thousands of words
  // without an index of all of them being rebuilt for every word erased.
  const layoutFor = useMemo(() => memoizeLayout(), []);
  const layout = layoutFor(page);

  const wordAt = useCallback(
    (id: string): WordType | undefined => {
      const element = page[layout.indexOf.get(id) ?? -1];
      return element && isWord(element) ? element : undefined;
    },
    [layout, page],
  );

  /**
   * The page grouped into paragraphs, each of which renders as its own block.
   *
   * Not cosmetic — the words sit exactly where `<br>` separators put them. It is
   * that all the words of an article otherwise share one inline formatting
   * context, so erasing a single word invalidates the layout of every other word
   * on the page. On a 4,600-word article that cost 8.6ms of layout per mark and
   * made erasing crawl; a paragraph at a time, it is 0.4ms.
   */
  const paragraphs = useMemo(
    () => layout.paragraphs.map(([start, end]) => page.slice(start, end) as WordType[]),
    [layout, page],
  );

  const isMarked = useCallback((id: string) => wordAt(id)?.isMarked, [wordAt]);

  const toggleById = useCallback(
    (id: string) => {
      dispatch({ type: "toggleWord", id });
    },
    [dispatch],
  );

  const editById = useCallback(
    (id: string) => {
      const word = wordAt(id);
      if (word) setEditedWord(word);
    },
    [wordAt],
  );

  const cursor = useWordCursor({
    wordIds: layout.wordIds,
    onToggle: toggleById,
    onEdit: editById,
  });
  const { setActiveId } = cursor;

  const activeIndex = cursor.activeId ? layout.indexOf.get(cursor.activeId) : undefined;
  const activeParagraph =
    activeIndex === undefined ? -1 : layout.paragraphAt[activeIndex];

  const onPaint = useCallback(
    (ids: string[], paint: boolean, transaction: string) => {
      dispatch({ type: "setWordsMarked", ids, isMarked: paint, transaction });
      void tickHaptic();
      // Keep the keyboard cursor where the reader last touched, so switching
      // from pointer to keyboard resumes from the same place.
      setActiveId(ids[ids.length - 1]);
    },
    [dispatch, setActiveId],
  );

  const onGestureEnd = useCallback(() => void endHaptics(), []);

  const { handlers } = useGesture({ onPaint, isMarked, onGestureEnd });

  /**
   * Hands the toolbar the page only when it asks for it.
   *
   * Every erased word produces a new page array, so passing it down re-rendered
   * the toolbar — a MUI SpeedDial and its nine actions — on every mark. The
   * toolbar's own props are all stable now, so it renders when undo and redo
   * become available and then stays put.
   */
  const pageRef = useRef(page);
  useEffect(() => {
    // After the commit, not during the render: an export only ever runs from a
    // click, which is always later than that.
    pageRef.current = page;
  }, [page]);
  const getPage = useCallback(() => pageRef.current, []);

  const undo = useCallback(() => dispatch({ type: "undo" }), [dispatch]);
  const redo = useCallback(() => dispatch({ type: "redo" }), [dispatch]);
  const cycleStyle = useCallback(() => dispatch({ type: "cycleStyle" }), [dispatch]);
  const adjustFontSize = useCallback(
    (delta: number) => dispatch({ type: "adjustFontSize", delta }),
    [dispatch],
  );

  const handleDoubleClick = useCallback(
    (event: React.MouseEvent) => {
      // Hit-test by coordinate rather than trusting `event.target`: the gesture
      // layer takes pointer capture, and a captured pointer retargets the
      // compatibility mouse events to the capturing element — so `target` here
      // is the content container, not the word under the cursor.
      const hit = window.document.elementFromPoint(event.clientX, event.clientY);
      const id = resolveElementId(hit) ?? resolveElementId(event.target as Element);
      const word = id ? wordAt(id) : undefined;
      if (word) setEditedWord(word);
    },
    [wordAt],
  );

  const saveCharacters = useCallback(
    (id: string, characters: Character[]) => {
      dispatch({ type: "setWordCharacters", id, characters });
    },
    [dispatch],
  );

  /**
   * Erases a random fraction of the words currently on screen. Reads the DOM
   * because "on screen" is a rendering fact, not something state knows.
   */
  const markRandom = useCallback(() => {
    const candidates = Array.from(
      window.document.querySelectorAll(`.word.unmarked[${WORD_ID_ATTRIBUTE}]`),
    ).filter(isElementInViewport);

    const ids = randomSample(candidates, RANDOM_ERASE_FRACTION)
      .map((element) => element.getAttribute(WORD_ID_ATTRIBUTE))
      .filter((id): id is string => Boolean(id));

    if (ids.length > 0) dispatch({ type: "setWordsMarked", ids, isMarked: true });
  }, [dispatch]);

  return (
    <div style={{ ...style.editor, marginTop: "50px" }}>
      {isPerfEnabled() && <PerfReadout words={layout.wordIds.length} />}

      {editedWord && (
        <WordEditor
          // Remount when a different word is opened so local edits reset.
          key={editedWord.id}
          word={wordAt(editedWord.id) ?? editedWord}
          save={saveCharacters}
          close={() => setEditedWord(null)}
          textStyle={style.text}
        />
      )}

      <div
        id={CONTENT_ELEMENT_ID}
        className={settling ? "page-in settling" : "page-in"}
        style={
          {
            ...style.content,
            // The gutters. The text column inside refuses touches outright so
            // a stroke is never taken away mid-drag; this padding around it is
            // left pannable, so a finger set down beside the text scrolls the
            // article natively, momentum and all.
            touchAction: "pan-y",
            "--mark-delay": markDelay(),
          } as CSSProperties
        }
        {...handlers}
        {...cursor.containerProps}
        onDoubleClick={handleDoubleClick}
      >
        {/*
          The text column: the one element that refuses touches, so a finger
          on the text erases in whatever direction it travels and the browser
          never takes the stroke away mid-drag. The padding around it — the
          gutters — is what stays pannable.

          One element, deliberately. A version that put `touch-action: none` on
          every word instead was the one users found sluggish on a phone. The
          browser keeps a region of every element that declares a touch-action
          and consults it for each touch, in the compositor rather than on the
          page's main thread — which is why no in-page measurement ever saw
          the cost of four thousand of them, and why this is the shape the app
          had when erasing felt fluid.
        */}
        <div role="presentation" style={{ touchAction: "none" }}>
          {paragraphs.map((words, index) => (
            <Paragraph
              key={index}
              words={words}
              textStyle={style.text}
              // Only the paragraph holding the cursor is told where it is, so
              // moving the cursor re-renders two paragraphs rather than all.
              activeId={activeParagraph === index ? cursor.activeId : undefined}
            />
          ))}
        </div>
      </div>

      <Actions
        getPage={getPage}
        title={meta.title ?? erasure.key}
        canUndo={canUndo}
        canRedo={canRedo}
        onUndo={undo}
        onRedo={redo}
        onCycleStyle={cycleStyle}
        onAdjustFontSize={adjustFontSize}
        onMarkRandom={markRandom}
        onCloseErasure={onClose}
      />
    </div>
  );
};

export default Editor;
